import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card, Text } from '@fragments';
import {
    IconCircleCheck,
    IconHome,
    IconPlayerPlayFilled,
    IconPlaylist,
} from '@tabler/icons-react';

import './styles.scss';

/**
 * PlaylistOutro — what closes the player's side panel once the current playlist
 * runs out. It resolves the *next* action from the section's playlist order and
 * renders exactly one of two cards, each under its own section title (the same
 * heading treatment as "Mais conteúdos nesta playlist"):
 *
 *  · **next** — another playlist follows: a forward card that jumps straight to
 *               its first unwatched video.
 *  · **done** — this was the last playlist: a completion card that hands the
 *               member back to the community root.
 *
 * @param {object}   playlist        The playlist currently listed in the panel.
 * @param {object}   course          The section (`section/:id` payload) it belongs to.
 * @param {function} onOpenPlaylist  Switches the panel to a playlist and opens its
 *                                   entry video — the same handler the selector uses.
 */
export default function PlaylistOutro({ playlist, course, onOpenPlaylist }) {
    const { t } = useTranslation('content');
    const navigate = useNavigate();
    const { slug } = useParams();

    // Singles aren't playlists — they never take part in the running order.
    const playlists = useMemo(
        () => (course?.content ?? []).filter((entry) => entry && !entry.single),
        [course?.content],
    );

    const nextPlaylist = useMemo(() => {
        if (!playlist?.id) return null;
        const index = playlists.findIndex((entry) => entry.id == playlist.id);
        if (index < 0) return null;
        return playlists.slice(index + 1).find((entry) => entry.contents?.length) ?? null;
    }, [playlists, playlist?.id]);

    const nextMeta = useMemo(() => {
        if (!nextPlaylist) return null;
        const contents = nextPlaylist.contents ?? [];
        const minutes = Math.round(
            contents.reduce((sum, content) => sum + (content.video?.duration || 0), 0) / 60,
        );
        return [
            t('playlist.videos', { count: contents.length }),
            minutes > 0 ? t('playlist.minutes', { count: minutes }) : null,
        ]
            .filter(Boolean)
            .join(' · ');
    }, [nextPlaylist, t]);

    // The splash takes the first three real banners it can find: the next
    // playlist's opening contents, or — for the completion card — a sweep of the
    // whole section, so the collage reads as "what you just finished".
    const nextPhotos = useMemo(
        () => pickBanners(nextPlaylist?.contents, nextPlaylist?.banner),
        [nextPlaylist],
    );

    const sectionPhotos = useMemo(
        () =>
            pickBanners(
                playlists.flatMap((entry) => entry.contents ?? []),
                course?.banner?.wide || course?.banner?.condensed,
            ),
        [playlists, course?.banner],
    );

    function handleOpenNext() {
        if (nextPlaylist) onOpenPlaylist?.(nextPlaylist);
    }

    function handleBackToCommunity() {
        navigate(`/${slug}`);
    }

    if (!playlist?.id || !playlists.length) return null;

    if (nextPlaylist) {
        return (
            <>
                <h3 className="content-suggestions-title mt-3 mb-1 bold">
                    {t('suggestions.outro.sequenceTitle')}
                </h3>

                <Card
                    interactive
                    onClick={handleOpenNext}
                    aria-label={t('suggestions.outro.nextAction')}
                    className="playlist-outro playlist-outro--next"
                >
                    <div className="playlist-outro__row">
                        <Splash photos={nextPhotos} fallback={<IconPlaylist />} />

                        <div className="playlist-outro__heading">
                            <Text.subheading contrast={6}>
                                {t('suggestions.outro.nextEyebrow')}
                            </Text.subheading>
                            <Text.h5 weight={500} contrast={9} maxLines={2}>
                                {nextPlaylist.name}
                            </Text.h5>
                            {nextMeta && <Text.caption contrast={5}>{nextMeta}</Text.caption>}
                        </div>

                        {/* Visual affordance only — the card owns the click and the focus. */}
                        <Button
                            color="inverse"
                            variant="rounded"
                            icon={<IconPlayerPlayFilled />}
                            tabIndex={-1}
                        />
                    </div>
                </Card>
            </>
        );
    }

    return (
        <>
            <h3 className="content-suggestions-title mt-3 mb-1 bold">
                {t('suggestions.outro.doneTitle')}
            </h3>

            <Card
                interactive
                onClick={handleBackToCommunity}
                aria-label={t('suggestions.outro.doneAction')}
                className="playlist-outro playlist-outro--done"
            >
                <div className="playlist-outro__stack">
                    <Splash photos={sectionPhotos} fallback={<IconCircleCheck />} muted />

                    <div className="playlist-outro__heading">
                        <Text.subheading contrast={6}>
                            {t('suggestions.outro.doneEyebrow')}
                        </Text.subheading>
                        <Text.h3 weight={500} contrast={9} maxLines={2}>
                            {course?.name}
                        </Text.h3>
                        <Text.p contrast={6}>{t('suggestions.outro.doneHint')}</Text.p>
                    </div>

                    <Button
                        fullWidth
                        color="inverse"
                        variant="rounded"
                        startIcon={<IconHome />}
                        text={t('suggestions.outro.doneAction')}
                        tabIndex={-1}
                    />
                </div>
            </Card>
        </>
    );
}

/**
 * Splash — the first three banners fanned out like dropped photos, each kept at
 * its natural aspect ratio and corner-clipped. The same three images are echoed
 * behind the stack, blurred and faint, so the collage sits on its own glow.
 * With nothing to show it degrades to a single tinted glyph chip.
 */
function Splash({ photos, fallback, muted = false }) {
    if (!photos.length) {
        return (
            <span className={'playlist-outro__chip' + (muted ? ' playlist-outro__chip--done' : '')}>
                {fallback}
            </span>
        );
    }

    return (
        <span className="playlist-outro__splash">
            <span className="playlist-outro__glow" aria-hidden="true">
                {photos.map((src, index) => (
                    <img key={index} src={src} alt="" />
                ))}
            </span>

            {photos.map((src, index) => (
                <img key={index} className="playlist-outro__photo" src={src} alt="" />
            ))}
        </span>
    );
}

/** The first three usable banners out of a content list, with a single fallback. */
function pickBanners(contents, fallbackBanner) {
    const banners = (contents ?? [])
        .map((content) => content?.banner)
        .filter(Boolean)
        .slice(0, 3);

    if (banners.length) return banners;
    return fallbackBanner ? [fallbackBanner] : [];
}
